import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authService, type User } from '@/lib/auth-service'

export const userQueryKey = ['auth', 'user'] as const

export function useUserQuery() {
  return useQuery<User | null>({
    queryKey: userQueryKey,
    queryFn: () => authService.fetchUser(),
    staleTime: 5 * 60_000,
    retry: false,
  })
}

export function useAuth() {
  const q = useUserQuery()
  return {
    user: q.data ?? null,
    isAuthenticated: !!q.data,
    isLoading: q.isPending,
  }
}

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authService.login(email, password),
    onSuccess: (user) => {
      qc.setQueryData(userQueryKey, user)
    },
  })
}

export function useRegister() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ email, password, name }: { email: string; password: string; name: string }) =>
      authService.register(email, password, name),
    onSuccess: (user) => {
      qc.setQueryData(userQueryKey, user)
    },
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      qc.setQueryData(userQueryKey, null)
      qc.removeQueries({ queryKey: ['documents'] })
    },
  })
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (email: string) => authService.requestPasswordReset(email),
  })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ token, password, email }: { token: string; password: string; email: string }) =>
      authService.resetPassword(token, password, email),
  })
}
